import { useEffect, useState } from "react"

import { useLazyGetAlertTypesQuery } from "store/api/widgetsApi"
import { ApiTypes } from "types"

import { HalfDoughnutSummaryWidget } from "components/widgets/HalfDoughnutSummaryWidget"

const CHART_CONFIG = [
  { key: "efficiency_issue" as const, label: "System Efficiency", color: "#f9a8d4" },
  { key: "air_quality" as const, label: "Air Quality", color: "#facc15" },
  { key: "fan_malfunction" as const, label: "Fan Malfunction", color: "#60a5fa" },
  { key: "ambient_sensor_malfunction" as const, label: "Ambient Sensor Malfunction", color: "#86efac" },
  { key: "humidity_sensor_malfunction" as const, label: "Humidity Sensor Malfunction", color: "#2dd4bf" },
  { key: "temperature_sensor_malfunction" as const, label: "Temperature Sensor Malfunction", color: "#7dd3fc" },
] as const

const dummyData: ApiTypes.Res.AlertTypesWidget = {
  air_quality: 4,
  ambient_sensor_malfunction: 12,
  fan_malfunction: 5,
  humidity_sensor_malfunction: 3,
  temperature_sensor_malfunction: 5,
  efficiency_issue: 46,
  devices_with_active_alerts: 75,
  temperature: 0,
}

const initialState: ApiTypes.Res.AlertTypesWidget = {
  air_quality: 0,
  ambient_sensor_malfunction: 0,
  fan_malfunction: 0,
  humidity_sensor_malfunction: 0,
  temperature_sensor_malfunction: 0,
  efficiency_issue: 0,
  devices_with_active_alerts: 0,
  temperature: 0,
}

interface WidgetMonitoringAlertsProps {
  dateRange: [string, string]
  unsupported?: boolean
}

export const WidgetMonitoringAlerts = ({ dateRange, unsupported = false }: WidgetMonitoringAlertsProps) => {
  const [triggerGetAlertTypes, { isFetching }] = useLazyGetAlertTypesQuery()
  const [widgetData, setWidgetData] = useState<ApiTypes.Res.AlertTypesWidget>(initialState)

  useEffect(() => {
    if (unsupported) {
      setWidgetData(dummyData)
      return
    }

    const fetchData = async () => {
      const res = await triggerGetAlertTypes({ date_start: dateRange[0], date_end: dateRange[1] })
      if (res?.data?.data) {
        setWidgetData(res.data.data)
      }
    }

    fetchData()
  }, [unsupported, dateRange, triggerGetAlertTypes])

  const data = unsupported ? dummyData : widgetData
  return <MonitoringAlertsWidget data={data} isFetching={!unsupported && isFetching} />
}

export const MonitoringAlertsWidget = ({
  data = initialState,
  isFetching = false,
}: {
  data?: Partial<ApiTypes.Res.AlertTypesWidget>
  isFetching?: boolean
}) => {
  const items = CHART_CONFIG.map(c => ({ label: c.label, color: c.color, value: data[c.key] ?? 0 }))
  const total = data.devices_with_active_alerts ?? items.reduce((sum, item) => sum + item.value, 0)

  return (
    <HalfDoughnutSummaryWidget
      title="Monitoring Alerts"
      mainLabel="Thermostats with alerts"
      items={items}
      mainValue={total}
      chartOptions={{ cutout: "60%", rotation: -95, circumference: 190 }}
      isFetching={isFetching}
      legendCenteredWrap
    />
  )
}
